import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import ListCreateButton from 'components/buttons/ListCreateButton';
import { BalanceOutlined as BalanceOutlinedIcon, CornerDownRightIcon } from 'components/icons';
import StatusChip from 'components/@extended/StatusChip';
import { DraftNameCell } from 'components/drafts/DraftNameCell';
import { useWithDrafts } from 'components/drafts/useWithDrafts';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useMemo } from 'react';
import { RecordLink } from 'components/record-chip/RecordLink';
import { Link as RouterLink } from 'react-router-dom';
import { ENTITY_LINK_CLASS } from 'pages/matters/util';
import { useLazyGetPracticeAreasListPageQuery } from 'store/api/practice-areas';
import { PracticeArea, PracticeAreaType } from 'types/practice-areas';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { formatPracticeAreaLabel } from 'utils/practiceAreaUtil';
import { nestedSubareaIds } from './nestedSubareaIds';

/** `isNested` rides on the row, so the columns never change with the data. */
type PracticeAreaRow = PracticeArea & { isNested?: boolean };

const getPracticeAreaHref = (practiceArea: PracticeArea) => `/practice-area/${practiceArea.id}`;

// With no column sort chosen, the list is in tree order: each parent, then its subareas.
const composeSort = (columnSort: string) => columnSort || 'hierarchy,asc';

const PracticeAreasTable = (): JSX.Element => {
    const navigateWithReturn = useNavigateWithReturnState();

    const [fetchPracticeAreas, { data, isFetching }] = useLazyGetPracticeAreasListPageQuery();
    const fetchData = useWithDrafts(fetchPracticeAreas);

    const rows = useMemo(() => {
        if (!data) return data;
        const nested = nestedSubareaIds(data.content);
        return {
            ...data,
            content: data.content.map((pa): PracticeAreaRow => (nested.has(pa.id) ? { ...pa, isNested: true } : pa)),
        };
    }, [data]);

    const columns = useMemo<DataTableColumnDef<PracticeAreaRow>[]>(
        () => [
            {
                header: t('practice-areas.name'),
                id: 'name',
                accessorKey: 'name',
                // The weights are the design's widths at a 1,152 px table. Name's is what the one-line AML and
                // Excluded headers leave it there, so the width those headers take comes out of Name alone.
                meta: {
                    widthWeight: 278,
                    minPx: 260,
                },
                cell: ({ cell }) => {
                    const practiceArea = cell.row.original;
                    const { id, isDraft, isNested, subareaCount } = practiceArea;
                    const isTopLevel = !practiceArea.parentPracticeArea;
                    return id ? (
                        <DraftNameCell draft={isDraft ? { type: 'practice-area', id } : null}>
                            {/* In the new design a cell's link takes its colour from what wraps it. */}
                            <div className="text-fg flex min-w-0 items-start gap-1.5">
                                {isNested ? (
                                    <span className="flex h-lh shrink-0 items-center">
                                        <CornerDownRightIcon className="text-grey-400 size-3.5" />
                                    </span>
                                ) : null}
                                <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
                                    <RouterLink
                                        className={cn(ENTITY_LINK_CLASS, isTopLevel ? 'font-semibold' : 'font-normal')}
                                        to={`/practice-area/${id}`}
                                    >
                                        {isDraft && !practiceArea.name
                                            ? t('new-entity.practice-area')
                                            : (formatPracticeAreaLabel(practiceArea) ?? '-')}
                                    </RouterLink>
                                    {isTopLevel && subareaCount ? (
                                        <span className="text-caption text-muted-foreground whitespace-nowrap">
                                            {t('practice-areas.subarea-count', { count: String(subareaCount) })}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </DraftNameCell>
                    ) : (
                        '-'
                    );
                },
            },
            {
                header: t('practice-areas.parent'),
                id: 'parentPracticeArea.name',
                accessorFn: (practiceArea) => practiceArea.parentPracticeArea?.name,
                meta: {
                    widthWeight: 220,
                    minPx: 160,
                },
                cell: ({ row }) => {
                    const parent = row.original.parentPracticeArea;
                    return parent?.id ? (
                        <RecordLink
                            className="entity-link hover:underline"
                            to={`/practice-area/${parent.id}`}
                            label={formatPracticeAreaLabel(parent)}
                        />
                    ) : null;
                },
            },
            {
                header: t('practice-areas.practice-group'),
                id: 'practiceGroup',
                enableSorting: false,
                meta: {
                    widthWeight: 190,
                    minPx: 140,
                },
                cell: ({ row }) => {
                    const group = row.original.practiceGroup;
                    return group?.id ? (
                        <RecordLink
                            className="entity-link hover:underline"
                            to={`/team/${group.id}`}
                            label={group.name}
                        />
                    ) : null;
                },
            },
            {
                header: t('practice-areas.aml-applicable'),
                id: 'isAmlApplicable',
                accessorKey: 'isAmlApplicable',
                meta: {
                    widthWeight: 110,
                    minPx: 100,
                },
                cell: ({ getValue }) => (getValue<PracticeArea['isAmlApplicable']>() ? t('yes') : t('no')),
            },
            {
                header: t('case-closing-practice-area.excluded-from-automatic-closing'),
                id: 'isExcludedFromAutomaticClosing',
                accessorKey: 'isExcludedFromAutomaticClosing',
                meta: {
                    widthWeight: 132,
                    minPx: 120,
                },
                cell: ({ getValue }) =>
                    getValue<PracticeArea['isExcludedFromAutomaticClosing']>() ? t('yes') : t('no'),
            },
            {
                header: t('practice-areas.active'),
                id: 'isActive',
                accessorKey: 'isActive',
                meta: {
                    widthWeight: 112,
                    minPx: 100,
                },
                cell: ({ getValue }) => {
                    switch (getValue<PracticeArea['isActive']>()) {
                        case false:
                            return <StatusChip label={t('not-active')} type="error" />;
                        case true:
                            return <StatusChip label={t('active')} type="success" />;
                        default:
                            return <StatusChip label={t('unknown')} type="inactive" />;
                    }
                },
            },
        ],
        []
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [
                {
                    selector: 'isAmlApplicable',
                    filters: [
                        {
                            label: t('practice-areas.aml-applicable'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    selector: 'isExcludedFromAutomaticClosing',
                    filters: [
                        {
                            label: t('case-closing-practice-area.excluded-from-automatic-closing'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    selector: 'practiceAreaTypeCode',
                    filters: [
                        {
                            label: t('practice-areas.show-only-parents'),
                            argument: PracticeAreaType.PRACTICE_AREA,
                        },
                    ],
                },
            ],
            filters: [],
            searchBarSelectors: [{ selector: 'name' }, { selector: 'parentPracticeArea.name' }],
        }),
        []
    );

    return (
        <ListTableWithFilters<PracticeAreaRow>
            getRowHref={getPracticeAreaHref}
            // Managed v8 engine for the weighted-width layout; column picker / reorder / resize
            // stay OFF (flags default false) — this table never had them.
            columnConfigId="practice-areas"
            columns={columns}
            composeSort={composeSort}
            data={rows}
            isLoading={isFetching}
            pluralTextId="practice-areas.practice-areas"
            singularTextId="practice-area"
            noEntriesId="practice-areas.no-areas-found"
            noEntriesBodyId="practice-areas.empty.body"
            noEntriesIcon={BalanceOutlinedIcon}
            noEntriesAction={
                <ListCreateButton
                    permissions={['CREATE.PRACTICE_AREAS']}
                    variant="contained"
                    color="secondary"
                    onClick={() => navigateWithReturn('/practice-area/new')}
                    labelId="practice-areas.add-new-practice-area"
                    newLabelId="new-entity.practice-area"
                />
            }
            fetchData={fetchData}
            filterConfig={filterConfig}
            rowsPerPage={50}
        />
    );
};

export default PracticeAreasTable;
